import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";
import { DemoBadge } from "@/components/common/DemoBadge";
import { IntegrationList } from "@/components/common/IntegrationList";
import { SecretsPanel } from "@/components/admin/SecretsPanel";
import { PartnersAdmin } from "@/components/admin/PartnersAdmin";
import { WaitlistAdmin } from "@/components/admin/WaitlistAdmin";
import { OffersAdmin } from "@/components/admin/OffersAdmin";
import { LegalAdmin } from "@/components/admin/LegalAdmin";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { t } from "@/i18n";

const COUNT_LABELS = { users: "Utilisateurs", demo_users: "Comptes démo", households: "Foyers", memberships: "Adhésions", organizations: "Organisations", audit_events: "Événements d'audit" };
const STATUS = { active: "Actif", foundation: "Fondations", planned: "Planifié" };

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-left text-sm">
      <thead><tr className="border-b border-border text-xs text-muted-foreground">{head.map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r) => <tr key={r[0]} className="border-b border-border/60">{r.slice(1).map((c, i) => <td key={i} className="py-2.5 pr-4 align-top">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const UsersTable = () => {
  const users = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  return (
      <section className="card-surface p-6">
        <h2 className="mb-3 text-lg font-bold">Utilisateurs</h2>
        {users.data ? <Table testid="admin-users-table" head={["Nom", "Email", "Rôle plateforme", "Démo"]}
          rows={users.data.map((u) => [u.id, u.name, u.email, u.platform_role ? t(`platformRoles.${u.platform_role}`) : "—", u.is_demo ? "Oui" : "Non"])} /> : <Skeleton className="h-24" />}
      </section>
  );
};

const OrgsTable = () => {
  const orgs = useQuery({ queryKey: ["admin-orgs"], queryFn: () => api.get("/admin/organizations").then((r) => r.data) });
  return (
    <section className="card-surface p-6">
      <h2 className="mb-3 text-lg font-bold">Organisations</h2>
      {orgs.data ? <Table testid="admin-orgs-table" head={["Nom", "Type", "Pays", "Membres", "Démo"]}
        rows={orgs.data.map((o) => [o.id, o.name, t(`orgTypes.${o.type}`), o.country, o.members, o.is_demo ? "Oui" : "Non"])} /> : <Skeleton className="h-24" />}
    </section>
  );
};

const AuditTable = () => {
  const audit = useQuery({ queryKey: ["admin-audit"], queryFn: () => api.get("/admin/audit").then((r) => r.data) });
  return (
      <section className="card-surface p-6">
        <h2 className="mb-3 text-lg font-bold">Journal d'audit</h2>
        {audit.data ? <Table testid="admin-audit-table" head={["Date", "Action", "Ressource", "Résultat"]}
          rows={audit.data.map((a) => [a.id, new Date(a.at).toLocaleString("fr-FR"), a.action, a.resource, a.allowed ? "Autorisé" : `Refusé (${a.reason})`])} /> : <Skeleton className="h-24" />}
      </section>
  );
};

const SECTIONS = {
  utilisateurs: ["Utilisateurs", UsersTable, "admin"],
  organisations: ["Organisations", OrgsTable, "admin"],
  partenaires: ["Partenaires", PartnersAdmin, "admin"],
  "liste-attente": ["Liste d'attente", WaitlistAdmin, "admin"],
  offres: ["Offres & projets", OffersAdmin, "admin"],
  juridique: ["Pages juridiques", LegalAdmin, "admin"],
  audit: ["Audit", AuditTable, "admin"],
  integrations: ["Intégrations", () => <div className="space-y-6">{useAuth().user.platform_role === "admin" && <SecretsPanel />}<section className="card-surface p-6"><IntegrationList /></section></div>, null],
};

export default function Admin() {
  const { user } = useAuth();
  const { section } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data) });
  if (!user.platform_role) return <Navigate to="/" replace />;
  if (section) {
    const s = SECTIONS[section];
    const role = user.platform_role === "demo_admin" ? "admin" : user.platform_role;
    if (!s || (s[2] && s[2] !== role)) return <Navigate to="/admin" replace />;
    const Comp = s[1];
    return <div className="space-y-6" data-testid={`admin-section-${section}`}><PageHeader eyebrow="CASHMY Admin" title={s[0]} /><Comp /></div>;
  }
  return (
    <div className="space-y-6" data-testid="admin-page">
      <PageHeader eyebrow="CASHMY Admin" title="Administration" text={t(`platformRoles.${user.platform_role}`)} actions={user.is_demo && <DemoBadge />} />
      {user.read_only && <p className="rounded-xl border border-dashed border-gold/60 bg-gold/10 p-3 text-sm" data-testid="admin-readonly-banner">Mode démo en lecture seule : vous voyez la console avec les données fictives uniquement. Aucune modification n'est possible.</p>}
      {isLoading || !data ? <Skeleton className="h-32 rounded-2xl" /> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {Object.entries(data.counts).map(([k, v]) => (
              <div key={k} className="card-surface p-4" data-testid={`admin-count-${k}`}>
                <div className="num text-2xl font-bold">{v}</div><div className="text-xs text-muted-foreground">{COUNT_LABELS[k]}</div>
              </div>
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="card-surface p-6">
              <h2 className="mb-3 text-lg font-bold">Modules</h2>
              <Table testid="admin-modules-table" head={["Module", "Brief", "Statut"]} rows={data.modules.map((m) => [m.id, m.name, `${m.brief}/8`, STATUS[m.status]])} />
            </section>
            <section className="card-surface p-6">
              <h2 className="mb-3 text-lg font-bold">Organisations par type</h2>
              <ul className="space-y-2 text-sm" data-testid="admin-orgs-by-type">
                {Object.entries(data.organizations_by_type).map(([k, v]) => <li key={k} className="flex justify-between"><span>{t(`orgTypes.${k}`)}</span><span className="num font-bold">{v}</span></li>)}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
